import {
  SetUserPrimaryDocument,
  StoreModel,
  UnassignUserFromStoreDocument,
  User,
  UsersDocument,
  StoresUserDetailsDocument,
  Actions,
  Subjects,
  AppArea,
} from 'src/graphql/generated/graphql';
import { UserRemove } from '@otp/icons';
import { Alert, ActionsColumn, Confirmation } from '@otp/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { getAddress } from 'src/utils/restaurant-data';
import { Star } from 'src/components/common/icons';
import { useCan } from 'src/utils/can';

type OTPsAssignedListActionsProps = {
  store: Partial<StoreModel>;
  user: Partial<User>;
  storeHasPrimary: boolean;
};

const QUERIES_TO_REFETCH = [UsersDocument, StoresUserDetailsDocument];

export const OTPsRestaurantsAssignedListActions = ({
  store,
  user,
  storeHasPrimary,
}: OTPsAssignedListActionsProps) => {
  const { can } = useCan();
  const canUnassign = can({
    action: Actions.Unassign,
    subject: Subjects.Stores,
    appArea: AppArea.Common,
  });
  const canMarkPrimary = can({
    action: Actions.MarkPrimary,
    subject: Subjects.Stores,
    appArea: AppArea.Common,
  });

  const [unassignMutation] = useMutation(UnassignUserFromStoreDocument, {
    refetchQueries: QUERIES_TO_REFETCH,
  });
  const [setUserPrimaryMutation] = useMutation(SetUserPrimaryDocument, {
    refetchQueries: QUERIES_TO_REFETCH,
  });
  const { t } = useTranslation(undefined, {
    keyPrefix: 'user-details.modals',
  });
  const [isUnassigning, setIsUnassigning] = useState(false);
  const [isPromoting, setIsPromoting] = useState(false);

  const onUnassign = () => {
    unassignMutation({
      variables: {
        userId: user.id,
        storeId: store.id,
      },
    });
    setIsUnassigning(false);
  };

  const onPromote = () => {
    setUserPrimaryMutation({
      variables: {
        userId: user.id,
        storeId: store.id,
      },
    });
    setIsPromoting(false);
  };

  return (
    <>
      <ActionsColumn
        icons={[
          ...(canUnassign
            ? [
                {
                  icon: <UserRemove />,
                  onClick: () => {
                    setIsUnassigning(true);
                  },
                },
              ]
            : []),

          ...(canMarkPrimary
            ? [
                {
                  icon: <Star isActive={!!store.isPrimary} />,
                  onClick: () => {
                    setIsPromoting(true);
                  },
                },
              ]
            : []),
        ]}
      />
      {store.isPrimary && (
        <>
          <Alert
            open={isUnassigning}
            onClose={() => setIsUnassigning(false)}
            message={
              <>
                <p>
                  {t('cannot-unassign-primary')} <b>{getAddress(store)}</b>.
                </p>
                <p>{t('restaurant-needs-primary')}</p>
              </>
            }
          />
          <Alert
            open={isPromoting}
            onClose={() => setIsPromoting(false)}
            message={t('cannot-promote-primary')}
          />
        </>
      )}
      {!store.isPrimary && (
        <>
          <Confirmation
            open={isUnassigning}
            onClose={() => setIsUnassigning(false)}
            onConfirm={onUnassign}
            message={t('confirm-unassign')}
          />
          <Confirmation
            open={isPromoting}
            onClose={() => setIsPromoting(false)}
            onConfirm={onPromote}
            message={
              storeHasPrimary
                ? t('confirm-change-primary')
                : t('confirm-promote')
            }
          />
        </>
      )}
    </>
  );
};
